import {
  type Filter,
  type Filters,
  MAX_2B_ASN,
  type TableFilter,
  type TableFilterOperator,
  displayTokensToLabelsAndFilters,
  filterOperators,
  isArrayContainsOperator,
  isLabelsArrayOperator,
  isLabelsTableFilter,
  isNameContainsAnyOperator,
  isNameNotContainsAnyOperator,
  nameTokensToLabelsAndFilters,
  parseArrayContainsFilterValue,
} from '@ipfabric/utils';
import type { RuleContext } from 'components/Table/components/AdvancedFilter/useRuleContext';
import { measureUnitValueConvertor } from 'components/Table/components/SimpleFilter/helpers/filterValueConvertors/measureUnitValueConvertor';
import { dateTimeToNumber } from 'components/Table/helpers/dateTimeToNumber';
import { uptimeToNumber } from 'components/Table/helpers/uptimeToNumber';
import type { RuleFilter } from './useAdvancedFilter';

const emptyTrue = {
  format: (): Filter => ['empty', true],
};

const emptyFalse = {
  format: (): Filter => ['empty', false],
};

type ApplicableRuleFilter = RuleFilter & {
  column: Exclude<RuleFilter['column'], null>;
  operator: Exclude<RuleFilter['operator'], null>;
};

const isApplicableRuleFilter = (
  filter: RuleFilter,
): filter is ApplicableRuleFilter =>
  filter.column !== null && filter.operator !== null;

const getBackendFilterValue = (
  filter: ApplicableRuleFilter,
  filterValue: Filter,
): Filter => {
  // Until there is only one kind of such key, keep hardcoded.
  if (filter.column.key === 'device.attributes') {
    return [filter.dynamicField, ...filterValue];
  }
  return filterValue;
};

const select = (
  filter: Exclude<RuleFilter, { column: null }>,
): Filters | undefined => {
  if (!isApplicableRuleFilter(filter)) {
    return undefined;
  }

  // Convert filter option values to number if filter optionKeyType is number
  // API expects number values for number select filters, e.g., status column in the duplex technology table
  if (
    filter.column.filter.type === 'select' &&
    filter.column.filter.optionKeyType === 'number' &&
    filterOperators.number.some((o) => o === filter.operator)
  ) {
    const unformattedValue = Number(filter.value.toString());
    const filterValue: Filter = [
      filter.operator,
      Number.isNaN(unformattedValue) ? filter.value : unformattedValue,
    ];
    return { [filter.column.key]: filterValue };
  }

  return generic(filter);
};

const labels = (
  filter: Exclude<RuleFilter, { column: null }>,
): Filters | undefined => {
  if (!isApplicableRuleFilter(filter)) {
    return undefined;
  }

  if (filterOperators.empty.some((o) => o === filter.operator)) {
    return generic(filter);
  }

  if (isNameContainsAnyOperator(filter.operator)) {
    return nameTokensToLabelsAndFilters(
      filter.column.key,
      'containsAny',
      filter.value,
    );
  }

  if (isNameNotContainsAnyOperator(filter.operator)) {
    return nameTokensToLabelsAndFilters(
      filter.column.key,
      'notContainsAny',
      filter.value,
    );
  }

  if (
    filter.operator === 'containsAny' ||
    filter.operator === 'notContainsAny'
  ) {
    return displayTokensToLabelsAndFilters(
      filter.column.key,
      filter.operator,
      filter.value,
    );
  }

  return generic(filter);
};

const generic = (
  filter: Exclude<RuleFilter, { column: null }>,
): Filters | undefined => {
  if (!isApplicableRuleFilter(filter)) {
    return undefined;
  }

  let filterValue: Filter;
  if (filter.operator === 'emptyTrue') {
    filterValue = emptyTrue.format();
  } else if (filter.operator === 'emptyFalse') {
    filterValue = emptyFalse.format();
  } else if (filter.operator === 'column') {
    filterValue = ['column', 'eq', filter.value];
  } else if (filter.operator === 'color') {
    filterValue = ['color', 'eq', filter.value];
  } else if (filter.operator === 'ncolumn') {
    filterValue = ['column', 'neq', filter.value];
  } else if (isArrayContainsOperator(filter.operator)) {
    if (isLabelsTableFilter(filter.column.filter)) {
      return labels(filter);
    }

    filterValue = [
      filter.operator,
      parseArrayContainsFilterValue(filter.value),
    ];
  } else if (isLabelsArrayOperator(filter.operator)) {
    return labels(filter);
  } else {
    filterValue = [filter.operator, filter.value];
  }

  return { [filter.column.key]: getBackendFilterValue(filter, filterValue) };
};

const uptime = (filter: RuleFilter): Filters | undefined => {
  if (!isApplicableRuleFilter(filter)) {
    return undefined;
  }

  if (filterOperators.uptime.some((o) => o === filter.operator)) {
    const filterValue: [TableFilterOperator, number | null] = [
      filter.operator,
      uptimeToNumber(filter.value),
    ];
    return { [filter.column.key]: filterValue };
  }
  return generic(filter);
};

const dateTime = (
  filter: RuleFilter,
  ruleContext: RuleContext,
): Filters | undefined => {
  if (!isApplicableRuleFilter(filter)) {
    return undefined;
  }

  if (filterOperators.dateTime.some((o) => o === filter.operator)) {
    if (filter.operator === 'nowGt' || filter.operator === 'nowLt') {
      return {
        [filter.column.key]: [
          'now',
          filter.operator === 'nowGt' ? 'gt' : 'lt',
          filter.value,
        ],
      };
    }
    const filterValue = dateTimeToNumber(filter.value, ruleContext);
    return { [filter.column.key]: [filter.operator, filterValue] };
  }
  return generic(filter);
};

const number = (filter: RuleFilter): Filters | undefined => {
  if (!isApplicableRuleFilter(filter)) {
    return undefined;
  }

  if (filterOperators.number.some((o) => o === filter.operator)) {
    const filterValue: Filter = [
      filter.operator,
      measureUnitValueConvertor.toNumber(filter.value),
    ];
    return { [filter.column.key]: filterValue };
  }
  return generic(filter);
};

const boolean = (filter: RuleFilter): Filters | undefined => {
  if (!isApplicableRuleFilter(filter)) {
    return undefined;
  }

  if (filterOperators.boolean.some((o) => o === filter.operator)) {
    const filterValue: Filter = [filter.operator, filter.value === 'true'];
    return { [filter.column.key]: filterValue };
  }
  return generic(filter);
};

const asn = (filter: RuleFilter): Filters | undefined => {
  if (!isApplicableRuleFilter(filter)) {
    return undefined;
  }

  if (filterOperators.asn.some((o) => o === filter.operator)) {
    const splittedValue = filter.value.split('.');

    const filterValue: Filter = [
      filter.operator,
      splittedValue.length === 2
        ? Number(splittedValue[0]) * MAX_2B_ASN + Number(splittedValue[1])
        : filter.value,
    ];
    return { [filter.column.key]: filterValue };
  }
  return generic(filter);
};

export const formatRule: {
  [Type in TableFilter['type']]: (
    filter: RuleFilter,
    ruleContext: RuleContext,
  ) => Filters | undefined;
} = {
  text: generic,
  number,
  select,
  uptime,
  ip: generic,
  ipv6: generic,
  boolean,
  asn,
  routing: generic,
  routingIpv6: generic,
  dateTime,
  object: generic,
  labels,
};
